/*
 * Event plants — date box + cart guard.
 *
 * One file shared by Deco Garden, Mashtela Online and HOYA (canonical copy:
 * decogarden-theme/event-plants/box/, copied verbatim into each theme's
 * assets/). Each theme supplies its own wording and colours through
 * snippets/event-plants-box.liquid (the box) and
 * snippets/event-plants-guard.liquid (rules JSON + the checkout dialog).
 *
 * No Hebrew lives in this file. Every visible word comes from the Liquid
 * markup (data-* attributes and <template> nodes); month and weekday names
 * come from Intl in he-IL. The GitHub->Shopify sync has silently skipped
 * liquid files with Hebrew inside JS strings before, so the assets follow
 * the same rule.
 *
 * Rules (owner, 2026-10-25): the customer picks the day they RECEIVE the
 * plants; 5 business days ahead (Sunday-Thursday, today counts as day 1
 * when it is a business day); Friday and Saturday are closed; sooner dates
 * go to WhatsApp; the flowering/herbs product needs 35 units in the cart,
 * types mixed freely. The customer also ticks the arrival window, always
 * 17:00-21:00 (owner, 2026-10-25), before the plants go into the cart.
 *
 * Self-pickup (owner, 2026-10-26): the 35 are for delivery only. A button
 * under the minimum switches the box to pickup: no minimum, the pickup hours
 * (Sunday-Thursday 10:00-17:00) instead of the window. Under 35 checkout
 * offers pickup only (the delivery calculators answer with no rate), so the
 * cart guard explains it and lets the customer go on with pickup.
 */
(function (root, factory) {
  var api = factory(root);
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else {
    root.EventPlants = api;
    api.boot();
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  var TZ = 'Asia/Jerusalem';
  var DAY_MS = 86400000;
  var DEFAULTS = { products: {}, leadDays: 5, closedWeekdays: [5, 6], closedDates: [], horizonDays: 365 };

  /* ── Dates: ISO yyyy-mm-dd strings, calendar math in UTC ─────────────── */

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  function isoFromUTC(d) {
    return d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate());
  }

  function utcFromIso(iso) {
    var p = String(iso).split('-');
    return new Date(Date.UTC(+p[0], +p[1] - 1, +p[2]));
  }

  /* Today in Israel, whatever the device's own time zone says. */
  function israelToday(now) {
    var d = now || new Date();
    try {
      var y = '', m = '', day = '';
      new Intl.DateTimeFormat('en-US', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' })
        .formatToParts(d)
        .forEach(function (p) {
          if (p.type === 'year') { y = p.value; } else if (p.type === 'month') { m = p.value; } else if (p.type === 'day') { day = p.value; }
        });
      if (y && m && day) { return y + '-' + m + '-' + day; }
    } catch (e) { /* very old engines: fall through */ }
    return isoFromUTC(new Date(d.getTime() + 3 * 3600000));
  }

  function addDays(iso, n) {
    return isoFromUTC(new Date(utcFromIso(iso).getTime() + n * DAY_MS));
  }

  function weekdayOf(iso) { return utcFromIso(iso).getUTCDay(); }

  function isClosedDay(iso, rules) {
    return (rules.closedWeekdays || []).indexOf(weekdayOf(iso)) !== -1 ||
      (rules.closedDates || []).indexOf(iso) !== -1;
  }

  /* The leadDays-th business day, counting today as day 1 when it is one
     (owner ruling 2026-08-04: a Tuesday 04.08 order -> Monday 10.08). */
  function earliestDate(todayIso, rules) {
    var lead = Math.max(1, rules.leadDays || 1);
    var d = todayIso;
    var counted = isClosedDay(d, rules) ? 0 : 1;
    var steps = 0;
    while (counted < lead && steps < 400) {
      d = addDays(d, 1);
      if (!isClosedDay(d, rules)) { counted += 1; }
      steps += 1;
    }
    return d;
  }

  function latestDate(todayIso, days) { return addDays(todayIso, days || 365); }

  /* A closed weekday wins over "too soon": no deliveries on Friday at all,
     whatever the lead time says. */
  function dayState(iso, ctx) {
    if (iso < ctx.today) { return 'past'; }
    if (iso > ctx.latest) { return 'beyond'; }
    if (isClosedDay(iso, ctx.rules)) { return 'closed'; }
    if (iso < ctx.earliest) { return 'soon'; }
    return 'open';
  }

  /* Sunday-first weeks; null pads the first and last week. */
  function monthCells(year, month0) {
    var lead = new Date(Date.UTC(year, month0, 1)).getUTCDay();
    var days = new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
    var cells = [];
    var i;
    for (i = 0; i < lead; i++) { cells.push(null); }
    for (i = 1; i <= days; i++) { cells.push(year + '-' + pad(month0 + 1) + '-' + pad(i)); }
    while (cells.length % 7) { cells.push(null); }
    return cells;
  }

  function shortfall(min, inCart, adding) {
    return Math.max(0, (min || 0) - (inCart || 0) - (adding || 0));
  }

  function fmtNumeric(iso) {
    var p = String(iso).split('-');
    return p[2] + '.' + p[1] + '.' + p[0];
  }

  function waHref(phone, text) {
    return 'https://wa.me/' + phone + (text ? '?text=' + encodeURIComponent(text) : '');
  }

  /* What stands between this cart and checkout. Quantities are summed per
     product across lines, so 20 flowering + 15 herbs = 35. A missing date
     blocks (kind 'date'); under the minimum the order can still go on, as
     pickup only (kind 'pickup'). */
  function cartStatus(cart, products) {
    var sums = {};
    var problems = [];
    ((cart && cart.items) || []).forEach(function (it) {
      var pid = String(it.product_id);
      if (!products || !products[pid]) { return; }
      var s = sums[pid] || (sums[pid] = { qty: 0, missingDate: false, title: it.product_title, url: it.url });
      s.qty += it.quantity || 0;
      if (!it.properties || !it.properties._event_date) { s.missingDate = true; }
    });
    Object.keys(sums).forEach(function (pid) {
      var s = sums[pid];
      var min = products[pid].min || 0;
      if (s.missingDate) {
        problems.push({ productId: pid, kind: 'date', have: s.qty, min: min, title: s.title, url: s.url });
      }
      if (min && s.qty > 0 && s.qty < min) {
        problems.push({ productId: pid, kind: 'pickup', have: s.qty, min: min, title: s.title, url: s.url });
      }
    });
    return {
      ok: problems.length === 0,
      problems: problems,
      blocking: problems.filter(function (p) { return p.kind === 'date'; }),
      pickup: problems.filter(function (p) { return p.kind === 'pickup'; }),
      sums: sums
    };
  }

  /* ── Browser part ─────────────────────────────────────────────────────── */

  var doc = root.document;
  var cfg = null;
  var boxes = [];
  var cartState = null;
  var cartTimer = null;
  var scanQueued = false;
  var originalFetch = null;

  function readConfig() {
    var el = doc.getElementById('event-plants-config');
    var out = {};
    var k;
    for (k in DEFAULTS) { if (Object.prototype.hasOwnProperty.call(DEFAULTS, k)) { out[k] = DEFAULTS[k]; } }
    if (!el) { return out; }
    try {
      var parsed = JSON.parse(el.textContent);
      for (k in parsed) { if (Object.prototype.hasOwnProperty.call(parsed, k)) { out[k] = parsed[k]; } }
    } catch (e) { /* keep defaults */ }
    return out;
  }

  function routesRoot() {
    var r = root.Shopify && root.Shopify.routes && root.Shopify.routes.root;
    return r || '/';
  }

  function fire(el, type) {
    try { el.dispatchEvent(new Event(type, { bubbles: true })); } catch (e) { /* old browsers */ }
  }

  function words(box) {
    var t = box.querySelector('template[data-epb-words]');
    var src = t && (t.content || t);
    return function (key) {
      var el = src && src.querySelector('[data-k="' + key + '"]');
      return el ? el.textContent.trim() : '';
    };
  }

  function dateFmt(opts) {
    try { return new Intl.DateTimeFormat('he-IL', Object.assign({ timeZone: 'UTC' }, opts)); } catch (e) { return null; }
  }
  var fmtWeekdayLong = dateFmt({ weekday: 'long' });
  var fmtWeekdayShort = dateFmt({ weekday: 'short' });
  var fmtWeekdayNarrow = dateFmt({ weekday: 'narrow' });
  var fmtMonth = dateFmt({ month: 'long', year: 'numeric' });
  var fmtFull = dateFmt({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  function fmt(f, iso, fallback) {
    return f ? f.format(utcFromIso(iso)) : fallback;
  }

  function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  function longLabel(iso) { return fmt(fmtWeekdayLong, iso, '') + ' ' + fmtNumeric(iso); }
  function shortLabel(iso) { return fmt(fmtWeekdayShort, iso, '') + ' · ' + fmtNumeric(iso); }

  var CHEVRON_RIGHT = '<svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M7.5 4.5 13 10l-5.5 5.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var CHEVRON_LEFT = '<svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M12.5 4.5 7 10l5.5 5.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function initBox(box) {
    if (box.getAttribute('data-ep-ready')) { return; }
    box.setAttribute('data-ep-ready', '1');

    var w = words(box);
    var pid = String(box.getAttribute('data-product-id') || '');
    var rules = { leadDays: cfg.leadDays, closedWeekdays: cfg.closedWeekdays, closedDates: cfg.closedDates };
    var today = israelToday();
    var serverToday = box.getAttribute('data-server-today') || '';
    /* A cached page carries a stale server day, a wrong device clock a stale
       browser day; the later one is the only safe floor against both. */
    if (/^\d{4}-\d{2}-\d{2}$/.test(serverToday) && serverToday > today) { today = serverToday; }
    var ctx = {
      today: today,
      earliest: earliestDate(today, rules),
      latest: latestDate(today, cfg.horizonDays),
      rules: rules
    };
    var min = (cfg.products && cfg.products[pid] && cfg.products[pid].min) || 0;
    var variantIds = String(box.getAttribute('data-variant-ids') || '').split(',').filter(Boolean);
    /* Inside the form, or beside it and tied to it by data-form-id (its
       hidden inputs then carry form="..."). */
    var formId = box.getAttribute('data-form-id');
    var form = box.closest('form') || (formId ? doc.getElementById(formId) : null);

    var el = function (name) { return box.querySelector('[data-epb-' + name + ']'); };
    var cal = el('cal');
    var outLabel = el('out-label');
    var outIso = el('out-iso');
    var picked = el('picked');
    var pickedLabel = el('picked-label');
    var errDate = el('error-date');
    var errWindow = el('error-window');
    var errMin = el('error-min');
    var soonNote = el('soon');
    var weekendNote = el('weekend');
    var wa = el('wa');
    var minRow = el('min');
    var minLeft = el('min-left');
    var live = el('live');
    /* Arrival window: one button per window the site offers (today only
       17:00-21:00). The customer ticks it themselves; a box without
       windows asks for none. */
    var slots = Array.prototype.slice.call(box.querySelectorAll('[data-epb-slot]'));
    var outWindow = el('out-window');
    var outWindowKey = el('out-window-key');
    var errors = { date: errDate, window: errWindow, min: errMin };
    /* Self-pickup: the [data-epb-method] buttons switch between delivery and
       pickup, and [data-epb-mode] parts show for one of them only. Only a
       product with a minimum offers it (pickup is how to buy fewer). */
    var methodButtons = Array.prototype.slice.call(box.querySelectorAll('[data-epb-method]'));
    var modeParts = Array.prototype.slice.call(box.querySelectorAll('[data-epb-mode]'));
    var outMethod = el('out-method');
    var outMethodKey = el('out-method-key');
    var pickupLabel = box.getAttribute('data-pickup-label') || '';
    var canPickup = min > 0 && methodButtons.length > 0;

    var state = { selected: '', window: '', method: 'delivery', view: ctx.earliest.slice(0, 7), inCart: 0 };
    var storeKey = 'epb:' + pid;
    var windowStoreKey = 'epb:window:' + pid;
    var methodStoreKey = 'epb:method:' + pid;
    try {
      var saved = root.sessionStorage.getItem(storeKey);
      if (saved && dayState(saved, ctx) === 'open') { state.selected = saved; state.view = saved.slice(0, 7); }
      var savedWindow = root.sessionStorage.getItem(windowStoreKey);
      if (savedWindow && slotFor(savedWindow)) { state.window = savedWindow; }
      if (canPickup && root.sessionStorage.getItem(methodStoreKey) === 'pickup') { state.method = 'pickup'; }
    } catch (e) { /* storage blocked: start empty */ }

    function isPickup() { return state.method === 'pickup'; }

    function slotFor(value) {
      for (var i = 0; i < slots.length; i++) {
        if (slots[i].getAttribute('data-epb-slot') === value) { return slots[i]; }
      }
      return null;
    }

    function slotLabel(value) {
      var slot = slotFor(value);
      return slot ? (slot.getAttribute('data-label') || value) : '';
    }

    Array.prototype.forEach.call(box.querySelectorAll('[data-epb-earliest]'), function (n) {
      n.textContent = longLabel(ctx.earliest);
    });

    var waGeneric = box.getAttribute('data-wa-text') || '';
    var waDated = box.getAttribute('data-wa-text-date') || '';
    var phone = box.getAttribute('data-wa-phone') || '';
    function setWa(iso) {
      if (!wa || !phone) { return; }
      var text = iso && waDated ? waDated.replace('{date}', fmtNumeric(iso)) : waGeneric;
      wa.setAttribute('href', waHref(phone, text));
    }
    setWa('');

    function say(msg) { if (live) { live.textContent = ''; live.textContent = msg; } }

    /* The red frame stays while any error is still showing. */
    function syncInvalid() {
      box.classList.toggle('is-invalid', [errDate, errWindow, errMin].some(function (n) { return n && !n.hidden; }));
    }

    function flash(node) {
      if (!node) { return; }
      node.classList.remove('is-flash');
      void node.offsetWidth; // restart the animation
      node.classList.add('is-flash');
      if (typeof node.scrollIntoView !== 'function') { return; }
      try { node.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (e) { node.scrollIntoView(false); }
    }

    function stateWord(s) {
      if (s === 'closed') { return w('closed'); }
      if (s === 'soon') { return w('soon'); }
      if (s === 'past' || s === 'beyond') { return w('unavailable'); }
      return '';
    }

    function render(focusIso) {
      var y = +state.view.slice(0, 4);
      var m0 = +state.view.slice(5, 7) - 1;
      var minView = ctx.today.slice(0, 7);
      var maxView = ctx.latest.slice(0, 7);
      var html = '';

      html += '<div class="epb__head">';
      html += '<button type="button" class="epb__nav" data-nav="-1" aria-label="' + esc(w('prev')) + '"' +
        (state.view <= minView ? ' disabled' : '') + '>' + CHEVRON_RIGHT + '</button>';
      html += '<p class="epb__month" aria-live="polite">' + esc(fmt(fmtMonth, state.view + '-01', state.view)) + '</p>';
      html += '<button type="button" class="epb__nav" data-nav="1" aria-label="' + esc(w('next')) + '"' +
        (state.view >= maxView ? ' disabled' : '') + '>' + CHEVRON_LEFT + '</button>';
      html += '</div>';

      html += '<div class="epb__grid" role="grid" aria-label="' + esc(w('grid')) + '">';
      html += '<div class="epb__row" role="row">';
      for (var wd = 0; wd < 7; wd++) {
        var sample = addDays('2026-11-01', wd); // 1.11.2026 is a Sunday
        var closedCol = (rules.closedWeekdays || []).indexOf(wd) !== -1;
        html += '<span class="epb__wd' + (closedCol ? ' is-closed' : '') + '" role="columnheader" aria-label="' +
          esc(fmt(fmtWeekdayLong, sample, '')) + '">' + esc(fmt(fmtWeekdayNarrow, sample, '')) + '</span>';
      }
      html += '</div>';

      var cells = monthCells(y, m0);
      var tabIso = (state.selected && state.selected.slice(0, 7) === state.view) ? state.selected : '';
      if (!tabIso) {
        for (var c = 0; c < cells.length && !tabIso; c++) {
          if (cells[c] && dayState(cells[c], ctx) === 'open') { tabIso = cells[c]; }
        }
      }
      if (focusIso && focusIso.slice(0, 7) === state.view) { tabIso = focusIso; }

      for (var i = 0; i < cells.length; i++) {
        if (i % 7 === 0) { html += (i ? '</div>' : '') + '<div class="epb__row" role="row">'; }
        var iso = cells[i];
        if (!iso) { html += '<span class="epb__pad" role="gridcell"></span>'; continue; }
        var s = dayState(iso, ctx);
        var sel = iso === state.selected;
        var cls = 'epb__day is-' + s + (sel ? ' is-selected' : '') + (iso === ctx.today ? ' is-today' : '');
        var label = fmt(fmtFull, iso, iso) + (stateWord(s) ? ', ' + stateWord(s) : '') + (sel ? ', ' + w('selected') : '');
        html += '<span role="gridcell"><button type="button" class="' + cls + '" data-iso="' + iso + '"' +
          ' aria-label="' + esc(label) + '"' +
          (s === 'open' ? ' aria-pressed="' + (sel ? 'true' : 'false') + '"' : ' aria-disabled="true"') +
          (s === 'past' || s === 'beyond' ? ' disabled' : '') +
          ' tabindex="' + (iso === tabIso ? '0' : '-1') + '">' + (+iso.slice(8)) + '</button></span>';
      }
      html += '</div></div>';
      cal.innerHTML = html;

      if (focusIso) {
        var f = cal.querySelector('[data-iso="' + focusIso + '"]');
        if (f && !f.disabled) { f.focus(); }
      }
    }

    /* quiet = restoring a saved choice on load: no focus jump, no announcement. */
    function select(iso, quiet) {
      state.selected = iso;
      if (outIso) { outIso.value = iso; outIso.disabled = false; }
      if (outLabel) { outLabel.value = shortLabel(iso); outLabel.disabled = false; }
      if (pickedLabel) { pickedLabel.textContent = longLabel(iso); }
      if (picked) { picked.hidden = false; }
      if (errDate) { errDate.hidden = true; }
      syncInvalid();
      try { root.sessionStorage.setItem(storeKey, iso); } catch (e) { /* fine */ }
      render(quiet ? '' : iso);
      if (!quiet) { say(w('picked') + ' ' + longLabel(iso)); }
    }

    /* The window travels with delivery lines only. */
    function syncWindowOutputs() {
      var on = !isPickup() && !!state.window;
      if (outWindowKey) { outWindowKey.value = on ? state.window : ''; outWindowKey.disabled = !on; }
      if (outWindow) { outWindow.value = on ? slotLabel(state.window) : ''; outWindow.disabled = !on; }
    }

    function pickWindow(value, quiet) {
      var slot = slotFor(value);
      if (!slot) { return; }
      state.window = value;
      slots.forEach(function (s) {
        s.classList.toggle('is-selected', s === slot);
        s.setAttribute('aria-checked', s === slot ? 'true' : 'false');
      });
      syncWindowOutputs();
      if (errWindow) { errWindow.hidden = true; }
      syncInvalid();
      try { root.sessionStorage.setItem(windowStoreKey, value); } catch (e) { /* fine */ }
      if (!quiet) { say(w('window-picked') + ' ' + slotLabel(value)); }
    }

    slots.forEach(function (s) {
      s.addEventListener('click', function () { pickWindow(s.getAttribute('data-epb-slot')); });
    });

    /* quiet = restoring on load. Pickup lines carry the method (the visible
       property named in the box, and `_event_method`=pickup); delivery lines
       carry neither (as before). */
    function setMethod(method, quiet) {
      state.method = canPickup && method === 'pickup' ? 'pickup' : 'delivery';
      var pickup = isPickup();
      box.classList.toggle('is-pickup', pickup);
      modeParts.forEach(function (n) { n.hidden = n.getAttribute('data-epb-mode') !== state.method; });
      if (outMethod) { outMethod.value = pickup ? pickupLabel : ''; outMethod.disabled = !pickup; }
      if (outMethodKey) { outMethodKey.value = pickup ? 'pickup' : ''; outMethodKey.disabled = !pickup; }
      syncWindowOutputs();
      if (pickup) {
        if (errWindow) { errWindow.hidden = true; }
        if (errMin) { errMin.hidden = true; }
      }
      syncInvalid();
      try { root.sessionStorage.setItem(methodStoreKey, state.method); } catch (e) { /* fine */ }
      applyMin();
      if (quiet) { return; }
      say(w(pickup ? 'pickup-on' : 'pickup-off'));
      /* The pressed button hides with its part; keyboard focus moves to the
         button that switches back. */
      var back = methodButtons.filter(function (b) {
        var part = b.closest('[data-epb-mode]');
        return !part || !part.hidden;
      })[0];
      if (back) { back.focus({ preventScroll: true }); }
    }

    methodButtons.forEach(function (b) {
      b.addEventListener('click', function () { setMethod(b.getAttribute('data-epb-method')); });
    });

    function goMonth(delta, focusIso) {
      var y = +state.view.slice(0, 4);
      var m0 = +state.view.slice(5, 7) - 1 + delta;
      var view = isoFromUTC(new Date(Date.UTC(y, m0, 1))).slice(0, 7);
      if (view < ctx.today.slice(0, 7) || view > ctx.latest.slice(0, 7)) { return; }
      state.view = view;
      render(focusIso);
    }

    cal.addEventListener('click', function (e) {
      var nav = e.target.closest('[data-nav]');
      if (nav) { goMonth(+nav.getAttribute('data-nav')); return; }
      var btn = e.target.closest('[data-iso]');
      if (!btn || btn.disabled) { return; }
      var iso = btn.getAttribute('data-iso');
      var s = dayState(iso, ctx);
      if (s === 'open') { select(iso); return; }
      if (s === 'closed') { flash(weekendNote); say(weekendNote ? weekendNote.textContent.trim() : ''); return; }
      if (s === 'soon') { setWa(iso); flash(soonNote); say(soonNote ? soonNote.textContent.trim() : ''); }
    });

    /* Arrow keys follow the RTL grid: left = the next day. */
    cal.addEventListener('keydown', function (e) {
      var btn = e.target.closest('[data-iso]');
      if (!btn) { return; }
      var iso = btn.getAttribute('data-iso');
      var step = { ArrowLeft: 1, ArrowRight: -1, ArrowDown: 7, ArrowUp: -7 }[e.key];
      var target = '';
      if (step) {
        target = addDays(iso, step);
      } else if (e.key === 'Home') {
        target = addDays(iso, -weekdayOf(iso));
      } else if (e.key === 'End') {
        target = addDays(iso, 6 - weekdayOf(iso));
      } else if (e.key === 'PageDown' || e.key === 'PageUp') {
        var d = utcFromIso(iso);
        var shifted = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + (e.key === 'PageDown' ? 1 : -1), 1));
        var last = new Date(Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, 0)).getUTCDate();
        shifted.setUTCDate(Math.min(d.getUTCDate(), last));
        target = isoFromUTC(shifted);
      } else {
        return;
      }
      e.preventDefault();
      if (target < ctx.today) { target = ctx.today; }
      if (target > ctx.latest) { target = ctx.latest; }
      if (target.slice(0, 7) !== state.view) {
        state.view = target.slice(0, 7);
        render(target);
      } else {
        var t = cal.querySelector('[data-iso="' + target + '"]');
        if (t) {
          Array.prototype.forEach.call(cal.querySelectorAll('[data-iso]'), function (b) { b.setAttribute('tabindex', '-1'); });
          t.setAttribute('tabindex', '0');
          t.focus();
        }
      }
    });

    /* ── Quantity: 35 for the flowering/herbs product, counting what the
       cart already holds, so a second type can be added in any amount. ── */
    /* getAttribute('id'), never form.id: product forms hold an input named
       "id" (the variant), which shadows the property. */
    function qtyInputs() {
      if (!form) { return []; }
      var found = Array.prototype.slice.call(form.querySelectorAll('input[name="quantity"]'));
      var formId = form.getAttribute('id');
      if (formId) {
        Array.prototype.forEach.call(doc.querySelectorAll('input[name="quantity"][form]'), function (inp) {
          if (inp.getAttribute('form') === formId && found.indexOf(inp) === -1) { found.push(inp); }
        });
      }
      return found;
    }

    function currentQty() {
      var inputs = qtyInputs();
      return inputs.length ? (parseInt(inputs[0].value, 10) || 0) : 1;
    }

    function updateMinHint() {
      if (!min || !minLeft) { return; }
      var left = isPickup() ? 0 : shortfall(min, state.inCart, currentQty());
      minLeft.hidden = left === 0;
      Array.prototype.forEach.call(box.querySelectorAll('[data-epb-left-n]'), function (n) { n.textContent = String(left); });
      Array.prototype.forEach.call(box.querySelectorAll('[data-epb-incart-n]'), function (n) { n.textContent = String(state.inCart); });
      var inCartNote = el('incart');
      if (inCartNote) { inCartNote.hidden = state.inCart === 0; }
      if (left === 0 && errMin && !errMin.hidden) { errMin.hidden = true; syncInvalid(); }
    }

    function applyMin() {
      if (!min) { if (minRow) { minRow.hidden = true; } return; }
      if (minRow) { minRow.hidden = false; }
      Array.prototype.forEach.call(box.querySelectorAll('[data-epb-min-n]'), function (n) { n.textContent = String(min); });
      var need = isPickup() ? 1 : Math.max(1, min - state.inCart);
      qtyInputs().forEach(function (inp) {
        inp.setAttribute('min', String(need));
        if (inp.dataset && inp.dataset.min !== undefined) { inp.dataset.min = String(need); }
        var max = parseInt(inp.getAttribute('max'), 10);
        if (max && max < 9999) {
          inp.setAttribute('max', '9999');
          if (inp.dataset && inp.dataset.max !== undefined) { inp.dataset.max = '9999'; }
        }
        if ((parseInt(inp.value, 10) || 0) < need) {
          inp.value = String(need);
          fire(inp, 'input');
          fire(inp, 'change');
        }
      });
      updateMinHint();
    }

    doc.addEventListener('input', function (e) {
      if (e.target && e.target.name === 'quantity' && qtyInputs().indexOf(e.target) !== -1) { updateMinHint(); }
    });
    doc.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'quantity' && qtyInputs().indexOf(e.target) !== -1) { updateMinHint(); }
    });

    /* In the order the customer meets them: the day, the window, the amount.
       Pickup asks for the day only. */
    function problems(addingQty) {
      var out = [];
      if (!state.selected || dayState(state.selected, ctx) !== 'open') { out.push('date'); }
      if (isPickup()) { return out; }
      if (slots.length && !state.window) { out.push('window'); }
      if (min && shortfall(min, state.inCart, addingQty) > 0) { out.push('min'); }
      return out;
    }

    function reveal(list) {
      Object.keys(errors).forEach(function (kind) {
        if (errors[kind]) { errors[kind].hidden = list.indexOf(kind) === -1; }
      });
      updateMinHint();
      box.classList.toggle('is-invalid', list.length > 0);
      var first = errors[list[0]];
      flash(first);
      say(first ? first.textContent.trim() : '');
      if (list[0] === 'date') {
        var target = cal.querySelector('.epb__day[tabindex="0"]');
        if (target) { target.focus({ preventScroll: true }); }
      } else if (list[0] === 'window') {
        if (slots[0]) { slots[0].focus({ preventScroll: true }); }
      } else {
        var q = qtyInputs()[0];
        if (q) { q.focus({ preventScroll: true }); }
      }
    }

    var entry = {
      box: box,
      form: form,
      productId: pid,
      variantIds: variantIds,
      propNames: {
        label: outLabel && outLabel.name,
        iso: outIso && outIso.name,
        window: outWindow && outWindow.name,
        windowKey: outWindowKey && outWindowKey.name,
        method: outMethod && outMethod.name,
        methodKey: outMethodKey && outMethodKey.name
      },
      isPickup: isPickup,
      needsWindow: function () { return slots.length > 0 && !isPickup(); },
      check: function (addingQty) { return problems(addingQty === undefined ? currentQty() : addingQty); },
      reveal: reveal,
      values: function () {
        if (!state.selected) { return null; }
        var pickup = isPickup();
        return {
          iso: state.selected,
          label: shortLabel(state.selected),
          window: pickup ? '' : state.window,
          windowLabel: pickup ? '' : slotLabel(state.window),
          method: pickup ? 'pickup' : '',
          methodLabel: pickup ? pickupLabel : ''
        };
      },
      message: function (list) {
        var n = errors[list[0]];
        return n ? n.textContent.trim() : '';
      },
      /* Everything is in order now: no error left on screen. */
      clear: function () {
        Object.keys(errors).forEach(function (kind) { if (errors[kind]) { errors[kind].hidden = true; } });
        syncInvalid();
      },
      setCart: function (qty) { state.inCart = qty; applyMin(); }
    };
    boxes.push(entry);

    if (state.selected) {
      select(state.selected, true);
    } else {
      render();
    }
    if (state.window) { pickWindow(state.window, true); }
    setMethod(state.method, true);
    if (cartState) { entry.setCart(qtyInCart(cartState, pid)); }
  }

  function qtyInCart(cart, pid) {
    var n = 0;
    ((cart && cart.items) || []).forEach(function (it) { if (String(it.product_id) === pid) { n += it.quantity || 0; } });
    return n;
  }

  function liveBoxes() {
    boxes = boxes.filter(function (b) { return b.box.isConnected; });
    return boxes;
  }

  function scanBoxes() {
    scanQueued = false;
    Array.prototype.forEach.call(doc.querySelectorAll('[data-event-plants-box]:not([data-ep-ready])'), initBox);
  }

  function queueScan() {
    if (scanQueued) { return; }
    scanQueued = true;
    (root.requestAnimationFrame || setTimeout)(scanBoxes);
  }

  /* The box that owns this add-to-cart form: its own form, or any other
     form on the page that adds one of its variants (sticky bars). */
  function entryForForm(f) {
    var list = liveBoxes();
    var i;
    for (i = 0; i < list.length; i++) { if (list[i].form === f) { return list[i]; } }
    if (!/\/cart\/add/.test(f.getAttribute('action') || '')) { return null; }
    var idInput = f.querySelector('[name="id"]');
    var vid = idInput ? String(idInput.value) : '';
    for (i = 0; i < list.length; i++) { if (vid && list[i].variantIds.indexOf(vid) !== -1) { return list[i]; } }
    return null;
  }

  function onAddSubmit(e) {
    var f = e.target;
    if (!f || f.tagName !== 'FORM') { return; }
    var entry = entryForForm(f);
    if (!entry) { return; }
    var qtyInput = f.querySelector('input[name="quantity"]');
    var adding = entry.form === f ? undefined : (qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1);
    var list = entry.check(adding);
    if (!list.length) {
      entry.clear();
      if (entry.form !== f) { carryProperties(entry, f); }
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    entry.reveal(list);
  }

  /* The line properties of the box's current choice, as [name, value]:
     the receive day (visible + ISO), then for delivery the arrival window
     (visible + key), for pickup the method (visible + key). */
  function propPairs(entry) {
    var v = entry.values();
    if (!v) { return []; }
    var n = entry.propNames;
    return [
      [n.iso, v.iso], [n.label, v.label],
      [n.windowKey, v.window], [n.window, v.windowLabel],
      [n.methodKey, v.method], [n.method, v.methodLabel]
    ].filter(function (pair) { return pair[0] && pair[1]; });
  }

  function bareName(name) { return String(name).replace(/^properties\[|\]$/g, ''); }

  /* A second form (sticky bar) adding the same product gets the chosen
     date and window copied in as hidden inputs, so its line carries them too. */
  function carryProperties(entry, f) {
    propPairs(entry).forEach(function (pair) {
      var inp = f.querySelector('input[name="' + pair[0] + '"]');
      if (!inp) {
        inp = doc.createElement('input');
        inp.type = 'hidden';
        inp.name = pair[0];
        f.appendChild(inp);
      }
      inp.value = pair[1];
    });
  }

  /* Last line of defence for themes that add to cart by calling fetch
     themselves: add the date and window when the box has them, refuse when
     it does not. */
  function inspectAdd(init) {
    var list = liveBoxes();
    if (!list.length || !init || !init.body) { return null; }
    var body = init.body;

    /* get(name) reads a property from the request by its form name. */
    function complete(entry, get) {
      if (!get(entry.propNames.iso)) { return false; }
      if (entry.isPickup()) { return !entry.propNames.methodKey || !!get(entry.propNames.methodKey); }
      return !entry.needsWindow() || !entry.propNames.windowKey || !!get(entry.propNames.windowKey);
    }

    function verdictFor(vid, qty, get, setProps) {
      for (var i = 0; i < list.length; i++) {
        var entry = list[i];
        if (entry.variantIds.indexOf(String(vid)) === -1) { continue; }
        if (complete(entry, get)) { return null; }
        var problemsList = entry.check(qty);
        if (problemsList.length) { return { entry: entry, problems: problemsList }; }
        setProps(entry);
        return null;
      }
      return null;
    }

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      return verdictFor(body.get('id'), parseInt(body.get('quantity'), 10) || 1,
        function (name) { return body.get(name); },
        function (entry) {
          propPairs(entry).forEach(function (pair) { body.set(pair[0], pair[1]); });
        });
    }

    if (typeof body === 'string') {
      var json = null;
      try { json = JSON.parse(body); } catch (e) { json = null; }
      if (json && typeof json === 'object') {
        var items = Array.isArray(json.items) ? json.items : [json];
        for (var j = 0; j < items.length; j++) {
          var item = items[j];
          var props = item.properties || {};
          var res = verdictFor(item.id, parseInt(item.quantity, 10) || 1,
            function (name) { return props[bareName(name)]; },
            function (entry) {
              item.properties = props;
              propPairs(entry).forEach(function (pair) { props[bareName(pair[0])] = pair[1]; });
            });
          if (res) { return res; }
        }
        init.body = JSON.stringify(json);
        return null;
      }
      var params = new URLSearchParams(body);
      var res2 = verdictFor(params.get('id'), parseInt(params.get('quantity'), 10) || 1,
        function (name) { return params.get(name); },
        function (entry) {
          propPairs(entry).forEach(function (pair) { params.set(pair[0], pair[1]); });
          init.body = params.toString();
        });
      return res2;
    }
    return null;
  }

  /* Other scripts wrap fetch too, sometimes after this one has run (seen on
     Deco Garden), which drops this wrapper; boot() calls this again after
     load so it ends up outermost. The checkout guard never depends on it. */
  function installFetchHook() {
    if (!root.fetch || root.fetch.__eventPlants) { return; }
    var inner = root.fetch;
    if (!originalFetch) { originalFetch = inner; }
    var wrapped = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isAdd = /\/cart\/add(\.js|\.json)?(\?|$)/.test(url);
      var isChange = isAdd || /\/cart\/(change|update|clear)(\.js|\.json)?(\?|$)/.test(url);
      if (isAdd) {
        var verdict = null;
        try { verdict = inspectAdd(init); } catch (e) { verdict = null; }
        if (verdict) {
          try { verdict.entry.reveal(verdict.problems); } catch (e) { /* the refusal stands */ }
          var description = verdict.entry.message(verdict.problems);
          return Promise.resolve(new Response(
            JSON.stringify({ status: 422, message: description, description: description }),
            { status: 422, headers: { 'Content-Type': 'application/json' } }
          ));
        }
      }
      var p = inner.apply(this, arguments);
      if (isChange) { p.then(refreshCartSoon, function () {}); }
      return p;
    };
    wrapped.__eventPlants = true;
    root.fetch = wrapped;
  }

  /* jQuery-era apps still post to /cart/* with XHR: refresh after them. */
  function installXhrHook() {
    var X = root.XMLHttpRequest;
    if (!X || X.prototype.__eventPlants) { return; }
    var open = X.prototype.open;
    X.prototype.open = function (method, url) {
      this.__epUrl = String(url || '');
      return open.apply(this, arguments);
    };
    var send = X.prototype.send;
    X.prototype.send = function () {
      if (/\/cart\/(add|change|update|clear)/.test(this.__epUrl || '')) {
        this.addEventListener('load', refreshCartSoon);
      }
      return send.apply(this, arguments);
    };
    X.prototype.__eventPlants = true;
  }

  function refreshCartSoon() {
    clearTimeout(cartTimer);
    cartTimer = setTimeout(refreshCart, 150);
  }

  function refreshCart() {
    var f = originalFetch || root.fetch;
    if (!f) { return; }
    f.call(root, routesRoot() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) { return; }
        cartState = cart;
        liveBoxes().forEach(function (b) { b.setCart(qtyInCart(cart, b.productId)); });
      })
      .catch(function () { /* keep the last known state */ });
  }

  /* ── Checkout guard ──────────────────────────────────────────────────── */

  var CHECKOUT = '[name="checkout"], a[href*="/checkout"], shopify-accelerated-checkout-cart, [data-event-plants-checkout]';

  /* The cart as it is at this very click. A synchronous request is the one
     answer that cannot be stale: the cached copy depends on hearing about
     every cart change, and themes and apps change the cart in ways this
     script never sees. It costs one short request on a checkout click. */
  function freshCart() {
    try {
      var x = new root.XMLHttpRequest();
      x.open('GET', routesRoot() + 'cart.js', false);
      x.setRequestHeader('Accept', 'application/json');
      x.send(null);
      if (x.status >= 200 && x.status < 300) { cartState = JSON.parse(x.responseText); }
    } catch (e) { /* keep the cached copy */ }
    return cartState;
  }

  function checkoutStatus() {
    if (!cfg || !cfg.products) { return null; }
    var cart = freshCart();
    if (!cart) { return null; } // unknown -> never block
    var st = cartStatus(cart, cfg.products);
    return st.ok ? null : st;
  }

  function within(node, sel) {
    try { return !!(sel && node && node.closest && node.closest(sel)); } catch (e) { return false; }
  }

  function firstVisible(sel) {
    if (!sel) { return null; }
    var list = [];
    try { list = doc.querySelectorAll(sel); } catch (e) { return null; }
    for (var i = 0; i < list.length; i++) {
      if (list[i].getClientRects && list[i].getClientRects().length) { return list[i]; }
    }
    return null;
  }

  /* After "go on with pickup", for a minute, checkout taps skip the notice
     (a missing date still stops them). */
  var pickupAckUntil = 0;

  function activate(next) {
    if (!next) { return; }
    if (next.tagName === 'FORM') {
      if (typeof next.requestSubmit === 'function') { next.requestSubmit(); } else { next.submit(); }
    } else if (typeof next.click === 'function') {
      next.click();
    }
  }

  /* Go on with pickup: the site's own pickup control when the page shows one
     (Deco Garden's cart toggle, HOYA's drawer pickup link), else the tap that
     was stopped, which reaches checkout with pickup as the only way. */
  function goPickup(target) {
    pickupAckUntil = Date.now() + 60000;
    activate(firstVisible(cfg && cfg.pickupSwitch) || target);
  }

  /* A missing date stops checkout. Under the minimum the order is pickup only:
     a pickup tap (cfg.pickupTargets) or a cart already set to pickup
     (cfg.pickupActive) goes straight on; anything else gets the notice. Once
     the customer chose to go on with pickup, a plain pay tap is turned into
     the site's pickup control when there is one. */
  function guardCheckout(e, target) {
    var st = checkoutStatus();
    if (!st) { return; }
    if (st.blocking.length) {
      e.preventDefault();
      e.stopImmediatePropagation();
      showDialog(st.blocking, 'blocking', target);
      return;
    }
    if (!st.pickup.length) { return; }
    if (within(target, cfg.pickupTargets)) { return; }
    try { if (cfg.pickupActive && doc.querySelector(cfg.pickupActive)) { return; } } catch (err) { /* bad selector: ask */ }
    if (Date.now() < pickupAckUntil) {
      var own = firstVisible(cfg.pickupSwitch);
      if (own && own !== target && !own.contains(target)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        activate(own);
      }
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    showDialog(st.pickup, 'pickup', target);
  }

  /* Clicks inside the notice are handled here and never reach the page's own
     handlers: a cart drawer takes them for a click outside it and closes
     (HOYA). Its [data-go] link still opens by itself. */
  function onDialogClick(e, dialog) {
    e.stopPropagation();
    if (e.target.closest('[data-pickup-go]')) {
      e.preventDefault();
      dialog.__epClose();
      goPickup(dialog.__epTarget);
    } else if (e.target.closest('[data-close]')) {
      e.preventDefault();
      dialog.__epClose();
    }
  }

  function onCheckoutClick(e) {
    var open = e.target && e.target.closest ? e.target.closest('dialog.epg') : null;
    if (open && open.__epClose) { onDialogClick(e, open); return; }
    var sel = CHECKOUT +
      (cfg && cfg.checkoutSelectors ? ', ' + cfg.checkoutSelectors : '') +
      (cfg && cfg.pickupTargets ? ', ' + cfg.pickupTargets : '');
    var t = e.target && e.target.closest ? e.target.closest(sel) : null;
    if (!t) { return; }
    guardCheckout(e, t);
  }

  function onCheckoutSubmit(e) {
    var f = e.target;
    if (!f || f.tagName !== 'FORM') { return; }
    var sub = e.submitter;
    var toCheckout = (sub && sub.name === 'checkout') || /\/checkout/.test(f.getAttribute('action') || '');
    if (!toCheckout) { return; }
    guardCheckout(e, sub || f);
  }

  /* mode 'blocking' or 'pickup': the template's [data-for] parts (title,
     buttons) for the other mode are dropped. */
  function showDialog(list, mode, target) {
    var tpl = doc.getElementById('event-plants-dialog');
    if (!tpl) { return; }
    var old = doc.querySelector('dialog.epg');
    if (old) { old.remove(); }
    var frag = (tpl.content || tpl).cloneNode(true);
    var dialog = frag.querySelector('dialog');
    Array.prototype.forEach.call(dialog.querySelectorAll('[data-for]'), function (n) {
      if (n.getAttribute('data-for') !== mode) { n.remove(); }
    });
    var lines = dialog.querySelector('[data-lines]');
    var proto = {};
    Array.prototype.forEach.call(dialog.querySelectorAll('[data-kind]'), function (n) {
      proto[n.getAttribute('data-kind')] = n;
      n.remove();
    });
    var put = function (scope, key, val) {
      Array.prototype.forEach.call(scope.querySelectorAll('[data-' + key + ']'), function (n) { n.textContent = String(val); });
    };
    list.forEach(function (p) {
      var row = proto[p.kind] && proto[p.kind].cloneNode(true);
      if (!row) { return; }
      put(row, 'have', p.have);
      put(row, 'min', p.min);
      put(row, 'left', Math.max(0, p.min - p.have));
      put(row, 'title', p.title || '');
      lines.appendChild(row);
    });
    if (list[0]) { put(dialog, 'left', Math.max(0, list[0].min - list[0].have)); }
    var go = dialog.querySelector('[data-go]');
    if (go && list[0] && list[0].url) { go.setAttribute('href', list[0].url); }
    doc.body.appendChild(dialog);
    var close = function () { if (dialog.open && dialog.close) { dialog.close(); } dialog.remove(); };
    /* Its buttons are handled in onCheckoutClick (see onDialogClick). */
    dialog.__epClose = close;
    dialog.__epTarget = target;
    var pickupGo = dialog.querySelector('[data-pickup-go]');
    dialog.addEventListener('cancel', function () { setTimeout(close, 0); });
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
      dialog.classList.add('epg--fallback');
    }
    var focusTarget = pickupGo || go || dialog.querySelector('button');
    if (focusTarget) { focusTarget.focus(); }
  }

  function boot() {
    if (!doc) { return; }
    var start = function () {
      cfg = readConfig();
      installFetchHook();
      installXhrHook();
      /* On window, capturing: these run before every document-level handler a
         theme adds. Deco Garden's cart.js sends the drawer's checkout link to
         checkout from a document capture listener of its own, registered
         before this script loads, so a document listener here came too late. */
      root.addEventListener('submit', onAddSubmit, true);
      root.addEventListener('submit', onCheckoutSubmit, true);
      root.addEventListener('click', onCheckoutClick, true);
      ['cart:change', 'cart:updated', 'cart:refresh', 'cart-drawer:open', 'theme:cart:change'].forEach(function (name) {
        doc.addEventListener(name, refreshCartSoon);
        doc.documentElement.addEventListener(name, refreshCartSoon);
      });
      root.addEventListener('pageshow', refreshCartSoon);
      root.addEventListener('load', function () {
        installFetchHook();
        setTimeout(installFetchHook, 2000);
      });
      scanBoxes();
      refreshCart();
      if (root.MutationObserver) {
        new MutationObserver(queueScan).observe(doc.documentElement, { childList: true, subtree: true });
      }
    };
    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }
  }

  return {
    israelToday: israelToday,
    addDays: addDays,
    weekdayOf: weekdayOf,
    isClosedDay: isClosedDay,
    earliestDate: earliestDate,
    latestDate: latestDate,
    dayState: dayState,
    monthCells: monthCells,
    shortfall: shortfall,
    cartStatus: cartStatus,
    fmtNumeric: fmtNumeric,
    waHref: waHref,
    boot: boot
  };
});
